import React, { useState, useRef } from 'react';
import { parseExcelSchedule } from './excelUtils';

interface ScheduleEvent {
  id: string;
  title: string;
  startTime: string; // Format: HH:MM
  endTime: string;   // Format: HH:MM
  location: string;
  date: string;      // Format: YYYY-MM-DD
  description?: string;
  type?: string;     // For color coding
}

interface ExcelScheduleImporterProps {
  onImport: (events: ScheduleEvent[]) => void;
  onError: (message: string) => void;
  onSuccess: (message: string) => void;
}

const ExcelScheduleImporter: React.FC<ExcelScheduleImporterProps> = ({ onImport, onError, onSuccess }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      handleFileSelected(droppedFile);
    }
  };
  
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      handleFileSelected(selectedFile);
    }
  };
  
  const handleFileSelected = (file: File) => {
    // Check if it's an Excel file
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      onError('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setFile(file);
    processExcelFile(file);
  };
  
  const handleBrowseClick = () => {
    // Trigger the hidden file input
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const processExcelFile = (file: File) => {
    setIsProcessing(true);
    setUploadProgress(10); // Start progress indicator
    
    const reader = new FileReader();
    
    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 50); // Max 50% for loading
        setUploadProgress(progress);
      }
    };
    
    reader.onload = (e) => {
      try {
        setUploadProgress(60); // Reading file complete, start parsing
        
        // Get the array buffer from the FileReader result
        const arrayBuffer = e.target?.result as ArrayBuffer;
        
        // Set timeout to allow UI to update with progress
        setTimeout(() => {
          try {
            setUploadProgress(80); // Parsing in progress
            
            // Use our utility function to parse the Excel file
            const result = parseExcelSchedule(arrayBuffer);
            
            if (result.errors.length > 0) {
              // Show the first error
              onError(result.errors[0]);
              setIsProcessing(false);
              setUploadProgress(0);
              return;
            }
            
            if (result.warnings.length > 0) {
              // Show any warnings along with success message
              const warningMsg = result.warnings.join(' ');
              onSuccess(`Imported ${result.events.length} events. Note: ${warningMsg}`);
            } else {
              onSuccess(`Successfully imported ${result.events.length} events from the Excel file.`);
            }
            
            // Call the onImport callback with the parsed events
            onImport(result.events);
            
            setUploadProgress(100);
            
            // Reset processing state after a delay to show 100% completion
            setTimeout(() => {
              setIsProcessing(false);
              setUploadProgress(0);
            }, 1000);
            
          } catch (parseErr) {
            console.error('Error parsing Excel data:', parseErr);
            onError('Failed to parse the Excel file. Please check the format.');
            setIsProcessing(false);
            setUploadProgress(0);
          }
        }, 300);
        
      } catch (err) {
        console.error('Error reading Excel file:', err);
        onError('Error reading the file. Please try again.');
        setIsProcessing(false);
        setUploadProgress(0);
      }
    };
    
    reader.onerror = () => {
      onError('Error reading the file. Please try again.');
      setIsProcessing(false);
      setUploadProgress(0);
    };
    
    reader.readAsArrayBuffer(file);
  };
  
  return (
    <div className="excel-importer">
      <h4>Import Schedule from Excel</h4>
      <p className="importer-description">
        Upload an Excel file (.xlsx or .xls) containing event schedule data. 
        The file should have columns for Event, Date Start/End, Time Start/End, and Room.
      </p>
      
      <div 
        className={`drop-zone ${isDragging ? 'active' : ''} ${isProcessing ? 'processing' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="drop-zone-content">
          {isProcessing ? (
            <div className="import-progress">
              <div className="icon-processing">⏳</div>
              <p>Processing your Excel file...</p>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${uploadProgress}%` }}></div>
              </div>
              <p className="progress-text">
                {uploadProgress < 50 ? 'Reading file...' : 
                  uploadProgress < 80 ? 'Parsing data...' : 
                    uploadProgress < 100 ? 'Processing events...' : 'Complete!'}
              </p>
            </div>
          ) : (
            <>
              <div className="icon-upload">📄</div>
              <p>Drag and drop an Excel file here, or <button type="button" className="browse-btn" onClick={handleBrowseClick}>browse</button></p>
              {file && <p className="file-name">Selected: {file.name}</p>}
            </>
          )}
        </div>
        
        <input 
          type="file"
          ref={fileInputRef}
          className="file-input"
          accept=".xlsx,.xls"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />
      </div>
      
      <div className="file-requirements">
        <h5>Excel File Requirements:</h5>
        <ul>
          <li>Must be an Excel file (.xlsx or .xls)</li>
          <li>Must have headers row with columns for: Event, Date/Date Start, Time Start/End, Room/Location</li>
          <li>Date formats should be in standard Excel date format</li>
          <li>The sheet "Plan" or the first sheet will be used for importing</li>
        </ul>
      </div>
    </div>
  );
};

export default ExcelScheduleImporter;